import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { api } from '../api';
import { BRAND_NAME } from '../brand';
import { useCatalogLabels, singularLower, capitalizeFirst } from '../catalogLabels';
import { todayMSKString, todayMSK, ymdLocal } from '../utils/timezone';
import AdminAddressAutocomplete from './AdminAddressAutocomplete';
import ProductSearchDropdown from './ProductSearchDropdown';
import QtyStepper from './QtyStepper';
import BouquetSearchDropdown from './BouquetSearchDropdown';
import AssemblyGroupBuilder from './AssemblyGroupBuilder';
import AdminCalendar from './AdminCalendar';
import { TrashIcon } from './ActionIcons';
import { useDeliverySlots, slotStartMinutes, slotGridCols } from '../deliverySlots';
import { deliveryOutOfRangeInfo, deliveryOutOfRangeText } from '../utils';

const MONTH_NAMES_SHORT = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const DAY_NAMES_SHORT = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

function parsePhoneDigits(raw) {
  let d = (raw || '').replace(/\D/g, '');
  if (d.length >= 1 && (d[0] === '7' || d[0] === '8')) d = d.slice(1);
  return d.slice(0, 10);
}

function displayPhone(digits) {
  if (!digits || digits.length === 0) return '';
  let r = '+7 (' + digits.slice(0, 3);
  if (digits.length >= 3) r += ') ';
  if (digits.length > 3) r += digits.slice(3, 6);
  if (digits.length > 6) r += '-' + digits.slice(6, 8);
  if (digits.length > 8) r += '-' + digits.slice(8, 10);
  return r;
}

function handlePhoneChange(raw, prev, setter) {
  let d = raw.replace(/\D/g, '');
  if (d.length >= 1 && (d[0] === '7' || d[0] === '8')) d = d.slice(1);
  d = d.slice(0, 10);
  if (prev && d === prev && raw.length < displayPhone(prev).length) d = d.slice(0, -1);
  setter(d);
}

function formatDisplayDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return `${d} ${MONTH_NAMES_SHORT[m - 1]} ${y}, ${DAY_NAMES_SHORT[date.getDay()]}`;
}

const OrderEditPanel = forwardRef(function OrderEditPanel({ order, onSave, onCancel, showToast, onSavingChange }, ref) {
  const catalogLabels = useCatalogLabels();
  const { timeSlots: configSlots } = useDeliverySlots();
  const [items, setItems] = useState([]);
  const [deliveryType, setDeliveryType] = useState('delivery');
  const [address, setAddress] = useState('');
  const [addressDadata, setAddressDadata] = useState('');
  const [cityDistrict, setCityDistrict] = useState('');
  const [addressVerified, setAddressVerified] = useState(true);
  const [apartment, setApartment] = useState('');
  const [noApartment, setNoApartment] = useState(false);
  const [entrance, setEntrance] = useState('');
  const [floor, setFloor] = useState('');
  const [intercom, setIntercom] = useState('');
  // «Не показывать адрес клиенту»: адрес уточнили у получателя — покупатель видит только «Доставка»
  const [hideAddress, setHideAddress] = useState(false);
  const [deliveryCoords, setDeliveryCoords] = useState(null);
  const [deliveryCost, setDeliveryCost] = useState(0);
  const [deliveryCostManual, setDeliveryCostManual] = useState(false);
  const [deliveryCalcLoading, setDeliveryCalcLoading] = useState(false);
  const [deliveryOutOfRange, setDeliveryOutOfRange] = useState(null); // { distance, maxKm }

  const [customerName, setCustomerName] = useState('');
  const [phone, setPhone] = useState('');
  const [isRecipientOther, setIsRecipientOther] = useState(false);
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');

  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');

  const [hasPostcard, setHasPostcard] = useState(false);
  const [greeting, setGreeting] = useState('');
  const [postcardPrice, setPostcardPrice] = useState(0);

  const [oddQuantity, setOddQuantity] = useState(false);
  const [packagingType, setPackagingType] = useState('transport');
  // Все типы упаковки из каталога (динамические pkg-N, не хардкод transport/gift)
  const [packagingList, setPackagingList] = useState([]);
  const [clientPaymentAmount, setClientPaymentAmount] = useState('');
  const [assemblyType, setAssemblyType] = useState('bunch');
  // Сборные букеты: [{sequence_no, florist_notes, items: [{product_id, product_name, price, quantity}]}]
  const [assemblies, setAssemblies] = useState([]);

  const [settings, setSettings] = useState({ service_fee_percent: 10, postcard_price: 199 });
  const [stockMap, setStockMap] = useState({});
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [showBouquetSearch, setShowBouquetSearch] = useState(false);
  const [expandedBouquetIdx, setExpandedBouquetIdx] = useState(null); // idx раскрытого редактора состава
  const [showCompSearchForIdx, setShowCompSearchForIdx] = useState(null); // idx где открыт search компонента
  const [productsCatalog, setProductsCatalog] = useState({}); // {pid: {name, price, step}}
  const [allProductsList, setAllProductsList] = useState(null); // сырой brief-список — отдаём в дропдауны, чтобы они не качали каталог заново
  const [isSaving, setIsSaving] = useState(false);
  // Сообщаем родителю о состоянии saving (для disabled-кнопки в footer модалки)
  useEffect(() => { if (onSavingChange) onSavingChange(isSaving); }, [isSaving, onSavingChange]);
  const [deficitConfirm, setDeficitConfirm] = useState(null); // { names, onConfirm }
  // Preview from backend POST /orders/{id}/preview-edit — источник истины для сумм
  // (правильный разнесённый сервисный сбор vs локальная единая ставка).
  const [preview, setPreview] = useState(null);
  const previewTimerRef = useRef(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [fullyBlockedDates, setFullyBlockedDates] = useState([]);
  const [blockedSlotsForDate, setBlockedSlotsForDate] = useState(null);
  const [coloredDates, setColoredDates] = useState({});
  const [maxQtyMap, setMaxQtyMap] = useState({});
  const [pendingDate, setPendingDate] = useState(null);
  const [showDateWarning, setShowDateWarning] = useState(false);
  const [dateManuallyChanged, setDateManuallyChanged] = useState(false);
  const calendarRef = useRef(null);

  // Today's date in YYYY-MM-DD (по МСК — как сервер; toISOString даёт UTC = ночью вчера)
  const todayStr = todayMSKString();

  // Initialize from order
  const isInternal = order?.admin_order_type === 'internal';

  useEffect(() => {
    if (!order) return;
    // Букеты qty>1 разбиваем на N строк qty=1 (каждая со своей копией snapshot),
    // чтобы редактировать состав каждого экземпляра отдельно.
    const expanded = [];
    // Сборка assemblies из order.items с assembly_sequence_no — отдельно от обычных items
    const asmMap = {};  // sequence_no → {sequence_no, florist_notes, items: [...]}
    (order.items || []).forEach(it => {
      // Стебли сборных групп идут в state `assemblies`, не в `items`
      if (it.assembly_sequence_no != null) {
        const seq = it.assembly_sequence_no;
        if (!asmMap[seq]) {
          asmMap[seq] = {
            sequence_no: seq,
            florist_notes: it.assembly_florist_notes || '',
            items: [],
          };
        }
        asmMap[seq].items.push({
          product_id: it.product_id,
          product_name: it.product_name,
          price: it.price,
          quantity: it.quantity,
        });
        return;
      }
      const baseRow = {
        product_id: it.product_id,
        bouquet_id: it.bouquet_id || null,
        bouquet_kind: it.bouquet_kind || null,
        product_name: it.product_name,
        price: it.price,
        step: it.step || productSteps[it.product_id] || 1,
        is_extra: it.is_extra || false,
        components_dirty: false,
      };
      // Своя цена: цена позиции ≠ сумме сырого снимка → компоненты для правки по СРЕДНЕЙ
      // (oi.price / кол-во стеблей), как считает бэк (_orig_bouquet_comp_prices). Иначе
      // (динамика) — сырой снимок. Без этого модалка показывала бы 200₽/шт (сырое), а
      // сохранялась бы средняя → рассинхрон «вижу 3600, сохранилось 4680».
      const _comps = it.bouquet_components || [];
      const _totalStems = _comps.reduce((s, c) => s + (c.quantity || 0), 0);
      const _rawSum = _comps.reduce((s, c) => s + (c.product_price || 0) * (c.quantity || 0), 0);
      const _avg = (it.bouquet_id && _totalStems > 0 && Math.abs((it.price || 0) - _rawSum) > 1)
        ? (it.price || 0) / _totalStems : null;
      const mapComp = (c) => ({
        product_id: c.product_id,
        product_name: c.product_name,
        product_price: _avg != null ? _avg : c.product_price,
        quantity: c.quantity,
        sort_order: c.sort_order || 0,
        is_replacement: c.is_replacement || false,
      });
      if (it.bouquet_id && (it.quantity || 1) > 1) {
        // split: N qty=1 строк, каждая с КОПИЕЙ snapshot.
        for (let i = 0; i < it.quantity; i++) {
          expanded.push({ ...baseRow, quantity: 1, bouquet_components: _comps.map(mapComp) });
        }
      } else {
        expanded.push({
          ...baseRow,
          quantity: it.quantity,
          bouquet_components: it.bouquet_id ? _comps.map(mapComp) : null,
        });
      }
    });
    setItems(expanded);
    setAssemblies(Object.values(asmMap).sort((a, b) => a.sequence_no - b.sequence_no));
    setDeliveryType(order.delivery_type || 'delivery');
    setAddress(order.delivery_address || '');
    setAddressDadata(order.delivery_address_dadata || '');
    setCityDistrict(order.city_district || '');
    setAddressVerified(order.address_verified !== false);
    setApartment(order.apartment || '');
    setNoApartment(order.no_apartment || false);
    setEntrance(order.entrance || '');
    setFloor(order.floor || '');
    setIntercom(order.intercom || '');
    setHideAddress(!!order.hide_address_from_client);
    // Подписка: клиентская доставка живёт в delivery_cost (0 при бесплатном режиме),
    // фолбэк на calculated_delivery_cost (тариф для курьера) давал фантомную доплату.
    setDeliveryCost(
      order.order_type === 'subscription'
        ? (order.delivery_cost || 0)
        : (order.delivery_cost || order.calculated_delivery_cost || 0)
    );
    // Для walkin/internal имя заказчика — в order.customer_name (user_id=NULL).
    // Дефолт "Администратор" если пусто (ставится на бэке при создании; для legacy
    // данных без имени — fallback в UI).
    setCustomerName(
      order.admin_order_type === 'walkin' || order.admin_order_type === 'internal'
        ? (order.customer_name || 'Администратор')
        : (order.user?.first_name || '')
    );
    setPhone(order.phone ? parsePhoneDigits(order.phone) : '');
    setIsRecipientOther(order.is_recipient_other || false);
    setRecipientName(order.recipient_name || '');
    setRecipientPhone(order.recipient_phone ? parsePhoneDigits(order.recipient_phone) : '');
    setDeliveryDate(order.delivery_date || '');
    setDeliveryTime(order.delivery_time || '');
    setDateManuallyChanged(false);
    setHasPostcard(!!(order.greeting || order.postcard_price > 0));
    setGreeting(order.greeting || '');
    setPostcardPrice(order.postcard_price || 0);
    setOddQuantity(order.odd_quantity || false);
    setPackagingType(order.packaging_type || 'transport');
    setClientPaymentAmount(order.client_payment_amount != null ? order.client_payment_amount : '');
    setAssemblyType(order.assembly_type || 'bunch');
  }, [order]);

  // Адрес заказа вне тарифной сетки — показываем это при открытии: иначе 0 ₽ у
  // такого заказа (№ 6046, 23.8 км при сетке до 21) выглядит как сбой расчёта.
  // Цену не трогаем — она уже согласована или её впишут вручную.
  useEffect(() => {
    setDeliveryOutOfRange(null);
    if (!order || order.delivery_type !== 'delivery' || order.order_type === 'subscription') return;
    if (!order.delivery_lat || !order.delivery_lon) return;
    let cancelled = false;
    api.calculateDelivery(order.delivery_address || '', order.delivery_lat, order.delivery_lon)
      .then(result => {
        if (cancelled || !result?.requires_negotiation) return;
        setDeliveryOutOfRange(deliveryOutOfRangeInfo(result));
        setDeliveryCostManual(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [order]);

  // Load settings, stock and product steps
  const [productSteps, setProductSteps] = useState({});

  useEffect(() => {
    api.getSettings().then(s => {
      setSettings({
        service_fee_percent: s.service_fee_percent || 10,
        bouquet_service_fee_percent: s.bouquet_service_fee_percent || 20,
        postcard_price: s.postcard_price || 199,
        subscription_delivery_mode: s.subscription_delivery_mode || 'free'
      });
    }).catch(() => {});
    api.getPackagingInventory().then(data => {
      setPackagingList(Array.isArray(data) ? data : []);
    }).catch(() => {});
    // brief=1: панели нужны только цены/шаги/остатки — без картинок и future_stock ответ в разы легче
    api.getProducts(1, 500, false, null, true).then(data => {
      const list = data.items || data;
      const stockM = {};
      const stepM = {};
      const catalog = {};
      list.forEach(p => {
        stockM[p.id] = p.quantity || 0;
        stepM[p.id] = p.step || 1;
        catalog[p.id] = { name: p.name, price: p.price, step: p.step || 1 };
      });
      setStockMap(stockM);
      setProductSteps(stepM);
      setProductsCatalog(catalog);
      setAllProductsList(list);
    }).catch(() => {});
    api.getFullyBlockedDates().then(r => setFullyBlockedDates(r.dates || [])).catch(() => {});
    if (order?.id) {
      api.getOrderDateAvailability(order.id, 60)
        .then(r => setColoredDates({ green: r.available_dates || [], red: r.unavailable_dates || [] }))
        .catch(() => {});
    }
  }, []);

  // Recalculate calendar dates based on cart items
  useEffect(() => {
    if (items.length === 0) return;
    const timer = setTimeout(() => {
      // Для календаря — только продуктовые позиции; букеты пропускаем (бэк handles bouquet availability отдельно).
      const cartItems = items.filter(i => !i.bouquet_id && i.product_id).map(i => ({ product_id: i.product_id, quantity: i.quantity || 1 }));
      api.getAvailableDates(cartItems, 30)
        .then(result => {
          const availDates = result.dates || [];
          const allDates = [];
          const today = todayMSK();
          for (let d = 0; d < 30; d++) {
            const dt = new Date(today);
            dt.setDate(dt.getDate() + d);
            allDates.push(ymdLocal(dt));
          }
          setColoredDates({
            green: availDates,
            red: [],
            yellow: allDates.filter(d => !availDates.includes(d))
          });
        })
        .catch(() => {});
    }, 500);
    return () => clearTimeout(timer);
  }, [items]);

  // Load maxQtyMap when deliveryDate changes.
  // Пустой product_ids = «все активные» на бэке: раньше эффект ждал stockMap из
  // /products/all и запросы шли водопадом — теперь оба стартуют параллельно при открытии.
  useEffect(() => {
    if (!deliveryDate || !order?.id) return;
    api.getAvailableMaxQtyBatch([], deliveryDate, order.id)
      .then(r => setMaxQtyMap(r.results || {}))
      .catch(() => {});
  }, [deliveryDate, order?.id]);

  // Patch item steps once product data is loaded
  useEffect(() => {
    if (Object.keys(productSteps).length === 0) return;
    setItems(prev => prev.map(it => ({
      ...it,
      step: it.step > 1 ? it.step : (productSteps[it.product_id] || 1)
    })));
  }, [productSteps]);

  // Refresh per-bouquet components_status при изменении состава cart или даты.
  // Передаём cart_items_pending = ВСЕ items, бэк сам вычтет демонд (фактически
  // включает «себя», но для inline-дефицита показываемое имеет смысл «сколько ещё
  // доступно сверх уже-добавленного» — это корректное представление конфликта).
  useEffect(() => {
    if (!deliveryDate) return;
    const bouquetItems = items.filter(it => it.bouquet_id);
    if (bouquetItems.length === 0) return;
    const cartPayload = items.map(it => it.bouquet_id ? ({
      bouquet_id: it.bouquet_id,
      quantity: it.quantity,
      components: (it.bouquet_components || []).map(c => ({ product_id: c.product_id, quantity: c.quantity })),
    }) : ({ product_id: it.product_id, quantity: it.quantity }));
    const timer = setTimeout(() => {
      api.getBouquetsAvailableMaxQtyBatch(
        bouquetItems.map(b => b.bouquet_id),
        deliveryDate,
        order?.id || null,
        cartPayload,
      ).then(res => {
        const results = res.results || {};
        setItems(prev => prev.map(it => {
          if (!it.bouquet_id) return it;
          const r = results[String(it.bouquet_id)];
          if (!r) return it;
          return {
            ...it,
            available_for_date: r.total_available,
            available_physical: r.physical_available,
            assembled_only: !!r.assembled_only,
            components_status: r.components || [],
          };
        }));
      }).catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [JSON.stringify(items.map(it => ({ b: it.bouquet_id, p: it.product_id, q: it.quantity }))), deliveryDate, order?.id]);

  // Load blocked slots when delivery date changes
  useEffect(() => {
    if (!deliveryDate) { setBlockedSlotsForDate(null); return; }
    api.getBlockedSlotsForDate(deliveryDate).then(r => {
      setBlockedSlotsForDate(r.blocked);
      // Не сбрасываем время автоматически — только если пользователь сам меняет дату (см. handleDateChange)
    }).catch(() => setBlockedSlotsForDate(null));
  }, [deliveryDate]);

  // Close calendar on click outside
  useEffect(() => {
    function handleClick(e) {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Calculate delivery cost when address changes
  // Номер расчёта: ответ для прежнего адреса, пришедший позже, цену не перезаписывает
  // (заказ № 590 Блума 29.10: адрес прежний, а цена 300 ₽ — от другого адреса)
  const recalcSeqRef = useRef(0);
  const recalcDelivery = useCallback(async (lat, lon) => {
    if (!lat || !lon) return;
    // Подписка с бесплатной доставкой: клиент не платит — тариф не подставляем
    // (бэкенд сам обновит calculated_delivery_cost для выплаты курьеру).
    if (order?.order_type === 'subscription'
        && (settings.subscription_delivery_mode || 'free') !== 'standard') return;
    const seq = ++recalcSeqRef.current;
    setDeliveryCalcLoading(true);
    try {
      const result = await api.calculateDelivery(address, lat, lon);
      if (seq !== recalcSeqRef.current) return;
      if (result.requires_negotiation) {
        // Вне сетки цены нет — раньше молча оставалась цена прежнего адреса.
        // Обнуляем и открываем поле: без цены сохранить не даст handleSave.
        setDeliveryOutOfRange(deliveryOutOfRangeInfo(result));
        setDeliveryCost(0);
        setDeliveryCostManual(true);
      } else {
        if (deliveryOutOfRange) setDeliveryCostManual(false);
        setDeliveryOutOfRange(null);
        if (result.price != null) setDeliveryCost(result.price);
      }
    } catch { /* keep old cost */ }
    finally { if (seq === recalcSeqRef.current) setDeliveryCalcLoading(false); }
  }, [address, order?.order_type, settings.subscription_delivery_mode, deliveryOutOfRange]);

  // --- Computed values ---
  const assemblyItemsCost = assemblies.reduce(
    (sum, g) => sum + g.items.reduce((s, it) => s + (it.price || 0) * (it.quantity || 0), 0),
    0
  );
  const itemsCost = items.reduce((sum, it) => sum + it.price * it.quantity, 0) + assemblyItemsCost;
  let subtotal;
  let extraCostForSub = 0;
  let extraStemsForSub = 0;
  let extraBouquetsForSub = 0;
  if (order?.order_type === 'subscription' && order?.subscription_price_per_delivery) {
    // is_extra — товары добавленные через редактирование (не из наполнения подписки)
    // Новые товары (без is_extra, но отсутствующие в order.items) тоже считаются extra.
    // Букеты в подписке — всегда extra, сбор по букетной ставке.
    const origProductIds = new Set((order?.items || []).filter(it => !it.bouquet_id).map(it => it.product_id));
    extraStemsForSub = items
      .filter(it => !it.bouquet_id && (it.is_extra || !origProductIds.has(it.product_id)))
      .reduce((sum, it) => sum + it.price * it.quantity, 0);
    extraBouquetsForSub = items
      .filter(it => it.bouquet_id)
      .reduce((sum, it) => sum + it.price * it.quantity, 0);
    extraCostForSub = extraStemsForSub + extraBouquetsForSub;
    subtotal = order.subscription_price_per_delivery + extraCostForSub;
  } else {
    subtotal = itemsCost;
  }
  const currentPostcardPrice = hasPostcard ? (postcardPrice || settings.postcard_price) : 0;
  const isSubscription = order?.order_type === 'subscription';
  const stemsFeePct = settings.service_fee_percent;
  const bouquetsFeePct = settings.bouquet_service_fee_percent;
  const feePercent = stemsFeePct;  // для отображения «(X%)» в детализации стеблей
  // Скидка подписки
  const subDiscountAmount = isSubscription
    ? Math.floor((order?.subscription_price_per_delivery || 0) * (order?.subscription_discount_percent || 0) / 100)
    : 0;
  // Сервисный сбор per-item:
  //   - стебли (loose, без assembly) → stems_pct
  //   - стебли в сборном букете       → bouquets_pct
  //   - каталожные букеты              → bouquets_pct
  const stemsItemsSubtotal = items.filter(it => !it.bouquet_id).reduce((s, it) => s + (it.price || 0) * (it.quantity || 0), 0);
  const bouquetItemsSubtotal = items.filter(it => it.bouquet_id).reduce((s, it) => s + (it.price || 0) * (it.quantity || 0), 0);
  const serviceFee = isSubscription && extraCostForSub === 0
    ? (order?.service_fee || 0)
    : isSubscription
      // База подписки + доп. стебли — единая ставка; букеты — букетная (как на бэке)
      ? Math.round((subtotal - extraBouquetsForSub - subDiscountAmount) * stemsFeePct / 100)
        + Math.round(extraBouquetsForSub * bouquetsFeePct / 100)
      : Math.round(stemsItemsSubtotal * stemsFeePct / 100) + Math.round((bouquetItemsSubtotal + assemblyItemsCost) * bouquetsFeePct / 100);
  const bonusUsed = order?.bonus_used || 0;
  // Подписка: delivery_cost не входит в total, бонусы не участвуют в расчёте доплаты
  const total = isSubscription
    ? (subtotal - subDiscountAmount + currentPostcardPrice + serviceFee)
    : (subtotal + currentPostcardPrice + serviceFee + deliveryCost - bonusUsed);

  const origTotal = isSubscription
    ? (order?.total || 0) + bonusUsed
    : (order?.total || 0);
  const currentClientPayment = clientPaymentAmount !== '' ? parseInt(clientPaymentAmount) || 0 : null;
  const origClientPayment = order?.client_payment_amount;
  // Для internal: доплата от client_payment_amount, для остальных от total
  const localDiff = isInternal
    ? (currentClientPayment != null ? currentClientPayment : 0) - (origClientPayment != null ? origClientPayment : 0)
    : total - origTotal;
  // Если preview с бэка пришёл — берём его значения (правильный двухставочный сбор).
  // Для internal оставляем локальный diff (он считается от client_payment_amount).
  const effServiceFee = preview?.service_fee != null ? preview.service_fee : serviceFee;
  const effTotal = preview?.total != null ? preview.total : total;
  const effDiff = isInternal ? localDiff : (preview?.diff != null ? preview.diff : localDiff);
  const diff = effDiff;
  const stemsFee = preview?.stems_service_fee != null ? preview.stems_service_fee : null;
  const bouquetsFee = preview?.bouquets_service_fee != null ? preview.bouquets_service_fee : null;
  const stemsPct = preview?.stems_service_fee_percent;
  const bouquetsPct = preview?.bouquets_service_fee_percent;

  // --- Backend preview (правильный разнесённый сервисный сбор) ---
  useEffect(() => {
    if (!order?.id) { setPreview(null); return; }
    const hasAny = (items && items.length > 0) || (assemblies && assemblies.length > 0);
    if (!hasAny) { setPreview(null); return; }
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(() => {
      const payload = {
        items: items.map(it => {
          const base = { quantity: it.quantity };
          if (it.bouquet_id) {
            base.bouquet_id = it.bouquet_id;
            if (it.components_dirty && it.bouquet_components) {
              base.bouquet_components = it.bouquet_components.map(c => ({
                product_id: c.product_id,
                quantity: parseInt(c.quantity) || 1,
                sort_order: c.sort_order || 0,
              }));
            }
          } else {
            base.product_id = it.product_id;
          }
          return base;
        }),
        assemblies: assemblies
          .filter(g => g.items && g.items.length > 0)
          .map((g, idx) => ({
            sequence_no: idx + 1,
            florist_notes: g.florist_notes && g.florist_notes.trim() ? g.florist_notes.trim() : null,
            items: g.items.map(it => ({
              product_id: it.product_id,
              quantity: it.quantity,
              price: it.price,
            })),
          })),
        postcard_price: hasPostcard ? (postcardPrice || settings.postcard_price) : 0,
        delivery_cost: deliveryCost,
        assembly_type: assemblyType,
      };
      api.previewEditOrder(order.id, payload)
        .then(setPreview)
        .catch(() => setPreview(null));
    }, 350);
    return () => { if (previewTimerRef.current) clearTimeout(previewTimerRef.current); };
  }, [items, assemblies, hasPostcard, postcardPrice, deliveryCost, order?.id, assemblyType]);

  // --- Handlers ---
  const handleAddressChange = (addr, data) => {
    setAddress(addr);
    if (data && data.geo_lat && data.geo_lon) {
      setDeliveryCoords({ lat: data.geo_lat, lon: data.geo_lon });
      setAddressDadata(data.unrestricted_value || addr);
      setCityDistrict(data.city_district || '');
      setAddressVerified(true);
      recalcDelivery(data.geo_lat, data.geo_lon);
    } else {
      setDeliveryCoords(null);
      setAddressVerified(false);
    }
  };

  const handleDeliveryTypeChange = (type) => {
    setDeliveryType(type);
    if (type === 'pickup') {
      // Не очищаем адрес — он сохраняется если переключат обратно на доставку
      api.getPickupPoints().then(points => {
        const active = points.find(p => p.is_active);
        if (active) setDeliveryCost(active.price || 0);
      }).catch(() => {});
    } else if (!deliveryCostManual) {
      // Обратно на доставку: после самовывоза в поле осталась цена точки (обычно 0),
      // и предпросмотр показывал «Доставка: 250 → 0» с возвратом. Возвращаем стоимость
      // заказа, а при проверенных координатах — пересчитываем по адресу (для подписки
      // с бесплатной доставкой recalcDelivery ничего не меняет, остаётся 0).
      setDeliveryCost(
        order?.order_type === 'subscription'
          ? (order?.delivery_cost || 0)
          : (order?.delivery_cost || order?.calculated_delivery_cost || 0)
      );
      if (deliveryCoords) recalcDelivery(deliveryCoords.lat, deliveryCoords.lon);
    }
  };

  // Available stock for an item on the current delivery date.
  // Available stock for an item.
  // For items already in the original order: stockMap + origQty (same as before).
  // For new items being added: use maxQtyMap (date-aware, includes planned stock).
  const getAvailable = (productId) => {
    const pidStr = String(productId);
    // Используем maxQtyMap (учитывает shelf_life, дату доставки, exclude_order_id)
    if (maxQtyMap[pidStr] !== undefined) {
      return maxQtyMap[pidStr];
    }
    // Fallback пока maxQtyMap загружается
    const origItem = (order?.items || []).find(it => it.product_id === productId);
    if (origItem) {
      return (stockMap[productId] ?? 0) + origItem.quantity;
    }
    return stockMap[productId] ?? 0;
  };

  const handleRemoveItem = (idx) => {
    // Последний обычный item можно удалить, если заказ не опустеет: остаётся сборный
    // букет (assemblies) или это подписка — то же условие, что в гарде handleSave.
    const hasAssemblyItems = assemblies.some(g => g.items && g.items.length > 0);
    if (items.length <= 1 && !hasAssemblyItems && order?.order_type !== 'subscription') {
      showToast('Заказ должен содержать хотя бы 1 товар', 'error');
      return;
    }
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleAddProduct = (product) => {
    const step = product.step || 1;
    const available = getAvailable(product.id);
    // Если есть в наличии но меньше шага — ставим доступное, иначе шаг
    const qty = available > 0 && available < step ? available : step;
    setItems(prev => [...prev, {
      product_id: product.id,
      product_name: product.name,
      quantity: qty,
      price: product.price,
      step,
      is_extra: order?.order_type === 'subscription'
    }]);
    setShowProductSearch(false);
  };

  const handleAddBouquet = (bouquet) => {
    // bouquet = {bouquet_id, name, price, kind, components: [...snapshot], available_for_date, available_physical, components_status: [...]}
    setItems(prev => [...prev, {
      product_id: null,
      bouquet_id: bouquet.bouquet_id,
      bouquet_kind: bouquet.kind || 'recipe',
      product_name: bouquet.name,
      price: bouquet.price,
      quantity: 1,
      bouquet_components: bouquet.components || [],
      components_dirty: false,
      // Для inline-дефицита в карточке позиции:
      available_for_date: bouquet.available_for_date ?? 0,
      available_physical: bouquet.available_physical ?? 0,
      assembled_only: !!bouquet.assembled_only,
      components_status: bouquet.components_status || [],
    }]);
    setShowBouquetSearch(false);
  };

  // === Assemblies (сборные букеты) handlers ===
  const handleAddAssembly = () => {
    setAssemblies(prev => [
      ...prev,
      { sequence_no: prev.length + 1, florist_notes: '', items: [] },
    ]);
  };
  const handleRemoveAssembly = (groupIdx) => {
    setAssemblies(prev => prev
      .filter((_, i) => i !== groupIdx)
      .map((g, i) => ({ ...g, sequence_no: i + 1 }))
    );
  };
  const handleAssemblyAddStem = (groupIdx, product) => {
    setAssemblies(prev => prev.map((g, i) =>
      i === groupIdx
        ? { ...g, items: [...g.items, {
            product_id: product.product_id || product.id,
            product_name: product.product_name || product.name,
            price: product.price,
            quantity: product.quantity || 1,
          }] }
        : g
    ));
  };
  const handleAssemblyRemoveStem = (groupIdx, stemIdx) => {
    setAssemblies(prev => prev.map((g, i) =>
      i === groupIdx ? { ...g, items: g.items.filter((_, k) => k !== stemIdx) } : g
    ));
  };
  const handleAssemblyChangeQty = (groupIdx, stemIdx, qty) => {
    setAssemblies(prev => prev.map((g, i) =>
      i === groupIdx
        ? { ...g, items: g.items.map((s, k) => k === stemIdx ? { ...s, quantity: qty } : s) }
        : g
    ));
  };
  const handleAssemblyChangeNotes = (groupIdx, notes) => {
    setAssemblies(prev => prev.map((g, i) =>
      i === groupIdx ? { ...g, florist_notes: notes } : g
    ));
  };

  // === Редактор компонентов букета ===
  const recalcBouquetPrice = (components) =>
    components.reduce((s, c) => s + (parseFloat(c.product_price) || 0) * (parseInt(c.quantity) || 0), 0);

  const setBouquetComponents = (itemIdx, newComponents) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== itemIdx) return it;
      return {
        ...it,
        bouquet_components: newComponents,
        components_dirty: true,
        // Автопересчёт цены позиции по компонентам (можно потом дать override-input)
        price: recalcBouquetPrice(newComponents),
      };
    }));
  };

  const handleAddBouquetComponent = (itemIdx, product) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== itemIdx) return it;
      // Уже есть такой компонент? — увеличиваем qty
      const existing = (it.bouquet_components || []).find(c => c.product_id === product.id);
      let newComps;
      if (existing) {
        newComps = it.bouquet_components.map(c =>
          c.product_id === product.id ? { ...c, quantity: c.quantity + 1, is_replacement: true } : c
        );
      } else {
        newComps = [...(it.bouquet_components || []), {
          product_id: product.id,
          product_name: product.name,
          product_price: product.price,
          quantity: 1,
          sort_order: (it.bouquet_components || []).length,
          is_replacement: true,
        }];
      }
      return { ...it, bouquet_components: newComps, components_dirty: true, price: recalcBouquetPrice(newComps) };
    }));
    setShowCompSearchForIdx(null);
  };

  const handleRemoveBouquetComponent = (itemIdx, compIdx) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== itemIdx) return it;
      const newComps = it.bouquet_components.filter((_, j) => j !== compIdx);
      return { ...it, bouquet_components: newComps, components_dirty: true, price: recalcBouquetPrice(newComps) };
    }));
  };

  const handleBouquetComponentQty = (itemIdx, compIdx, newQty) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== itemIdx) return it;
      const newComps = it.bouquet_components.map((c, j) =>
        j === compIdx ? { ...c, quantity: Math.max(1, parseInt(newQty) || 1), is_replacement: true } : c
      );
      return { ...it, bouquet_components: newComps, components_dirty: true, price: recalcBouquetPrice(newComps) };
    }));
  };

  const handleDateChange = (newDate) => {
    if (newDate < todayStr) return;
    // Красные даты (полностью заблокированные) — предупреждение
    const isUnavailable = (coloredDates.red || []).includes(newDate);
    if (isUnavailable && newDate !== order?.delivery_date) {
      setPendingDate(newDate);
      setShowDateWarning(true);
      setShowCalendar(false);
      return;
    }
    setDeliveryDate(newDate);
    setDeliveryTime('');
    setDateManuallyChanged(true);
    setShowCalendar(false);
  };

  const confirmDateChange = () => {
    if (pendingDate) {
      setDeliveryDate(pendingDate);
      setDeliveryTime('');
      setDateManuallyChanged(true);
    }
    setPendingDate(null);
    setShowDateWarning(false);
  };

  const cancelDateChange = () => {
    setPendingDate(null);
    setShowDateWarning(false);
  };

  const handlePostcardToggle = (checked) => {
    setHasPostcard(checked);
    if (checked) {
      setPostcardPrice(settings.postcard_price);
    } else {
      setPostcardPrice(0);
      setGreeting('');
    }
  };

  // Экспортируем triggerSave для родителя (кнопка теперь в footer модалки)
  useImperativeHandle(ref, () => ({
    triggerSave: () => handleSave(),
  }));

  const handleSave = () => {
    const hasAssemblyItems = assemblies.some(g => g.items && g.items.length > 0);
    if (items.length === 0 && !hasAssemblyItems && order?.order_type !== 'subscription') { showToast('Добавьте хотя бы один товар', 'error'); return; }
    if (!deliveryDate) { showToast('Укажите дату доставки', 'error'); return; }
    // Прошлая дата запрещена ТОЛЬКО если её меняют. Если оставили исходную (заказ из прошлого
    // редактируется — например меняют имя), то проверка не нужна.
    const originalDate = order?.delivery_date || '';
    const dateChanged = deliveryDate !== originalDate;
    if (dateChanged && deliveryDate < todayStr) { showToast('Новая дата доставки не может быть в прошлом', 'error'); return; }
    // Слот обязателен и для доставки, и для самовывоза (клиент должен знать когда забрать)
    if (!deliveryTime) { showToast('Выберите время доставки', 'error'); return; }
    if (deliveryType === 'delivery' && !address) { showToast('Укажите адрес доставки', 'error'); return; }
    if (deliveryType === 'delivery' && !noApartment && !apartment.trim()) { showToast('Укажите квартиру или отметьте "нет"', 'error'); return; }
    // Адрес сменили на адрес вне сетки — без цены доставки не сохраняем (как при
    // создании). Уже сохранённый 0 у старого адреса не блокируем: доставку могли
    // согласовать бесплатной, а правят, например, время.
    if (deliveryType === 'delivery' && deliveryCoords && deliveryOutOfRange
        && order?.admin_order_type !== 'internal' && !(Number(deliveryCost) > 0)) {
      showToast('Адрес вне зоны доставки — укажите стоимость доставки', 'error'); return;
    }
    if (!phone && order?.admin_order_type !== 'internal' && order?.admin_order_type !== 'walkin') { showToast('Укажите телефон клиента', 'error'); return; }
    if (isRecipientOther && !recipientName?.trim()) { showToast('Укажите имя получателя', 'error'); return; }
    if (isRecipientOther && !recipientPhone) { showToast('Укажите телефон получателя', 'error'); return; }
    // Источники резервирования — авто-резолв на бэке (resolve_stock_sources DESC по плановым).
    const stemDeficits = items.filter(it => !it.bouquet_id && it.quantity > getAvailable(it.product_id));
    // Букеты: дефицит per-component (avail_excl_self = backend.avail + self_demand).
    // Дефицит по букетам — группами: букет один раз, под ним недостающие
    // стебли (в окне подтверждения рисуются двумя колонками, а не строкой
    // «Букет → Стебель» на каждую позицию).
    const bouquetDeficitGroups = [];
    items.forEach(it => {
      if (!it.bouquet_id) return;
      const cs = it.components_status || [];
      if (!cs.length) return;
      const qty = parseInt(it.quantity) || 1;
      // Собранные из партий (lots) стеблей не требуют — дефицит только на recipe-часть.
      const physAvail = it.available_physical != null
        ? it.available_physical
        : (it.bouquet_kind === 'assembled' ? qty : 0);
      const needRecipe = Math.max(0, qty - Math.min(qty, physAvail));
      if (needRecipe <= 0) return;
      const lines = [];
      cs.forEach(c => {
        const perB = c.needed_per_bouquet || 0;
        const availExclSelf = (c.available_on_date || 0) + perB * qty;
        const recipeNeeded = perB * needRecipe;
        const def = Math.max(0, recipeNeeded - Math.max(0, availExclSelf));
        if (def > 0) lines.push({ name: c.name, deficit: def });
      });
      if (lines.length) bouquetDeficitGroups.push({ title: it.product_name, lines });
    });
    // assembled_only: букет «только готовые» — добор по рецепту разрешён, но предупреждаем.
    const assembledOnlyWarnings = [];
    items.forEach(it => {
      if (!it.bouquet_id || !it.assembled_only) return;
      const qty = parseInt(it.quantity) || 1;
      const phys = it.available_physical != null ? it.available_physical : 0;
      const over = qty - phys;
      if (over > 0) assembledOnlyWarnings.push(`${it.product_name}: «только готовые» — готовых ${Math.max(0, phys)} шт, ещё ${over} соберётся по рецепту`);
    });
    const names = [
      ...stemDeficits.map(it => {
        const avail = getAvailable(it.product_id);
        return `${it.product_name} — дефицит ${it.quantity - avail} шт`;
      }),
      ...assembledOnlyWarnings,
    ];
    if (names.length > 0 || bouquetDeficitGroups.length > 0) {
      setDeficitConfirm({ names, groups: bouquetDeficitGroups, onConfirm: () => { setDeficitConfirm(null); doSave(); } });
      return;
    }
    doSave();
  };

  const doSave = async () => {
    setIsSaving(true);
    try {
      const data = {
        items: items.map(it => {
          const base = { quantity: it.quantity };
          if (it.bouquet_id) {
            base.bouquet_id = it.bouquet_id;
            // Передаём bouquet_components только если состав менялся —
            // иначе бэк возьмёт текущий snapshot (без перезаписи is_replacement).
            if (it.components_dirty && it.bouquet_components) {
              base.bouquet_components = it.bouquet_components.map(c => ({
                product_id: c.product_id,
                quantity: parseInt(c.quantity) || 1,
                sort_order: c.sort_order || 0,
              }));
            }
          } else {
            base.product_id = it.product_id;
          }
          return base;
        }),
        delivery_type: deliveryType,
        delivery_date: deliveryDate,
        delivery_time: deliveryTime,
        delivery_cost: deliveryCost,
        customer_name: customerName,
        phone: phone ? displayPhone(phone) : null,
        is_recipient_other: isRecipientOther,
        recipient_name: isRecipientOther ? recipientName : null,
        recipient_phone: isRecipientOther && recipientPhone ? displayPhone(recipientPhone) : null,
        greeting: hasPostcard ? greeting : '',
        postcard_price: currentPostcardPrice,
        odd_quantity: oddQuantity,
        packaging_type: packagingType,
        assembly_type: assemblyType,
        assemblies: assemblies
          .filter(g => g.items && g.items.length > 0)
          .map((g, idx) => ({
            sequence_no: idx + 1,
            florist_notes: g.florist_notes && g.florist_notes.trim() ? g.florist_notes.trim() : null,
            items: g.items.map(it => ({
              product_id: it.product_id,
              quantity: it.quantity,
              price: it.price,
            })),
          })),
        ...(isInternal && { client_payment_amount: currentClientPayment != null ? currentClientPayment : 0 }),
      };

      if (deliveryType === 'delivery') {
        data.delivery_address = address;
        data.delivery_address_dadata = addressDadata;
        data.city_district = cityDistrict;
        data.address_verified = addressVerified;
        data.apartment = noApartment ? null : (apartment || null);
        data.no_apartment = noApartment;
        data.entrance = entrance;
        data.floor = floor;
        data.intercom = intercom;
        data.hide_address_from_client = hideAddress;
        if (deliveryCoords) {
          data.delivery_lat = deliveryCoords.lat;
          data.delivery_lon = deliveryCoords.lon;
        }
      }

      // Источники резервирования резолвятся бэком автоматически (resolve_stock_sources).
      await api.editOrder(order.id, data);
      showToast('Заказ обновлён');
      onSave();
    } catch (e) {
      showToast(e.message || 'Ошибка сохранения', 'error');
    } finally { setIsSaving(false); }
  };

  // --- Changes summary ---
  const changes = [];
  const origItems = order?.items || [];
  // Стебли (product_id) и букеты (bouquet_id) обрабатываем раздельно — у букетов product_id=null.
  // Стебли сборных групп (assembly_sequence_no) исключаем — они в стейте `assemblies`, а не в `items`,
  // иначе бы все они ложно помечались как «Удалён».
  const origStems = origItems.filter(it => !it.bouquet_id && it.assembly_sequence_no == null);
  const newStems = items.filter(it => !it.bouquet_id);
  const origStemsMap = {};
  origStems.forEach(it => { origStemsMap[it.product_id] = it; });
  const newStemsMap = {};
  newStems.forEach(it => { newStemsMap[it.product_id] = it; });

  // Стебли: добавлены / удалены / изменено количество
  origStems.forEach(oi => {
    if (!newStemsMap[oi.product_id]) {
      changes.push({ text: `Удалён: ${oi.product_name} x${oi.quantity} (${oi.price * oi.quantity}₽)`, type: 'remove' });
    }
  });
  newStems.forEach(ni => {
    if (!origStemsMap[ni.product_id]) {
      changes.push({ text: `Добавлен: ${ni.product_name} x${ni.quantity} (${ni.price * ni.quantity}₽)`, type: 'add' });
    }
  });
  newStems.forEach(ni => {
    const oi = origStemsMap[ni.product_id];
    if (oi && oi.quantity !== ni.quantity) {
      const costDiff = (ni.quantity - oi.quantity) * ni.price;
      changes.push({ text: `${ni.product_name}: ${oi.quantity} → ${ni.quantity} (${costDiff > 0 ? '+' : ''}${costDiff}₽)`, type: 'change' });
    }
  });

  // Букеты: группируем per bouquet_id, сравниваем подписи экземпляров (multiset diff).
  const origBouquetsByBid = {};
  origItems.forEach(it => {
    if (it.bouquet_id) (origBouquetsByBid[it.bouquet_id] = origBouquetsByBid[it.bouquet_id] || []).push(it);
  });
  const newBouquetsByBid = {};
  items.forEach(it => {
    if (it.bouquet_id) (newBouquetsByBid[it.bouquet_id] = newBouquetsByBid[it.bouquet_id] || []).push(it);
  });
  const bouquetSig = (inst) =>
    (inst.bouquet_components || []).map(c => `${c.product_id}:${c.quantity}`).sort().join(',');

  const allBids = new Set([...Object.keys(origBouquetsByBid), ...Object.keys(newBouquetsByBid)]);
  allBids.forEach(bid => {
    const orig = origBouquetsByBid[bid] || [];
    const fresh = newBouquetsByBid[bid] || [];
    const name = (orig[0] || fresh[0])?.product_name || '';
    const origCount = orig.reduce((s, i) => s + (i.quantity || 1), 0);
    const newCount = fresh.reduce((s, i) => s + (i.quantity || 1), 0);

    // Полный add/remove букета
    if (origCount === 0 && newCount > 0) {
      changes.push({ text: `Добавлен букет: ${name} ×${newCount}`, type: 'add' });
      return;
    }
    if (newCount === 0 && origCount > 0) {
      changes.push({ text: `Удалён букет: ${name} ×${origCount}`, type: 'remove' });
      return;
    }
    if (origCount !== newCount) {
      changes.push({ text: `${name}: ${origCount} → ${newCount} шт`, type: 'change' });
    }

    // Multiset compositions diff per экземпляр
    const sigCount = (arr) => arr.reduce((m, s) => { m[s] = (m[s] || 0) + 1; return m; }, {});
    const origC = sigCount(orig.map(bouquetSig));
    const newC = sigCount(fresh.map(bouquetSig));
    const removed = [];
    const added = [];
    Object.entries(origC).forEach(([sig, n]) => {
      const newN = newC[sig] || 0;
      for (let i = 0; i < (n - newN); i++) removed.push(sig);
    });
    Object.entries(newC).forEach(([sig, n]) => {
      const origN = origC[sig] || 0;
      for (let i = 0; i < (n - origN); i++) added.push(sig);
    });
    // Кэш components по sig (для lookup имён)
    const compsBySig = {};
    orig.forEach(i => { compsBySig[bouquetSig(i)] = i.bouquet_components || []; });
    fresh.forEach(i => { compsBySig[bouquetSig(i)] = i.bouquet_components || []; });

    // Парим removed с added — diff в составе одного экземпляра
    const pairs = Math.min(removed.length, added.length);
    for (let i = 0; i < pairs; i++) {
      const oldComps = compsBySig[removed[i]] || [];
      const newComps = compsBySig[added[i]] || [];
      const oldDict = {}, newDict = {};
      oldComps.forEach(c => { oldDict[c.product_id] = c; });
      newComps.forEach(c => { newDict[c.product_id] = c; });
      const compDiffs = [];
      new Set([...Object.keys(oldDict), ...Object.keys(newDict)]).forEach(pid => {
        const o = oldDict[pid], n = newDict[pid];
        const compName = (n || o)?.product_name || '';
        if (!n) compDiffs.push(`− ${compName} ×${o.quantity}`);
        else if (!o) compDiffs.push(`+ ${compName} ×${n.quantity}`);
        else if (o.quantity !== n.quantity) compDiffs.push(`~ ${compName}: ${o.quantity} → ${n.quantity}`);
      });
      if (compDiffs.length) {
        changes.push({ text: `${name}: ${compDiffs.join('; ')}`, type: 'change' });
      }
    }
    // Leftover (без пары) — это полный экземпляр добавлен/убран сверх qty diff
    for (let i = pairs; i < removed.length; i++) {
      const comps = compsBySig[removed[i]] || [];
      const parts = comps.map(c => `${c.product_name} ×${c.quantity}`);
      changes.push({ text: `${name}: − экземпляр (${parts.join(', ')})`, type: 'remove' });
    }
    for (let i = pairs; i < added.length; i++) {
      const comps = compsBySig[added[i]] || [];
      const parts = comps.map(c => `${c.product_name} ×${c.quantity}`);
      changes.push({ text: `${name}: + экземпляр (${parts.join(', ')})`, type: 'add' });
    }
  });

  if ((order?.delivery_cost || 0) !== deliveryCost) {
    changes.push({ text: `Доставка: ${order?.delivery_cost || 0}₽ → ${deliveryCost}₽`, type: 'change' });
  }
  if (deliveryType !== (order?.delivery_type || 'delivery')) {
    changes.push({ text: `Тип: ${deliveryType === 'pickup' ? 'Самовывоз' : 'Доставка'}`, type: 'change' });
  }
  if (currentPostcardPrice !== (order?.postcard_price || 0)) {
    const origPP = order?.postcard_price || 0;
    changes.push({ text: currentPostcardPrice > 0 ? `Открытка добавлена (+${currentPostcardPrice}₽)` : `Открытка убрана (−${origPP}₽)`, type: 'change' });
  }
  if (packagingType !== (order?.packaging_type || 'transport')) {
    const pkgLabel = packagingType === 'none'
      ? 'Без коробки'
      : (packagingList.find(p => p.type === packagingType)?.name || packagingType);
    changes.push({ text: `Упаковка: ${pkgLabel}`, type: 'change' });
  }
  // Сравниваем с тем же значением, что выставили при инициализации (для walkin/internal —
  // customer_name с дефолтом «Администратор»), иначе появляется фантомное «Имя: — → Администратор».
  const initialCustomerName =
    (order?.admin_order_type === 'walkin' || order?.admin_order_type === 'internal')
      ? (order?.customer_name || 'Администратор')
      : (order?.user?.first_name || '');
  if (customerName !== initialCustomerName) {
    changes.push({ text: `Имя: ${initialCustomerName || '—'} → ${customerName}`, type: 'change' });
  }

  // Белая карточка с тонкой рамкой — как состав букета в каталоге (был серый фон с полосой слева)
  const sectionStyle = { padding: '14px 16px', background: '#fff', border: '1px solid #D8DDDA', '--ch': '10px', '--stroke': '#D8DDDA' };
  const sectionTitleStyle = { fontSize: 11, fontWeight: 700, color: '#1C3D2E', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Items */}
      <div style={sectionStyle}>
        <div style={{ ...sectionTitleStyle, color: '#1C3D2E' }}>Состав заказа</div>
        {isSubscription && items.some(it => !it.is_extra) && (
          <div style={{ fontSize: 11, color: '#1C3D2E', fontWeight: 600, marginBottom: 2 }}>Товары подписки</div>
        )}
        {/* Для подписки: base-наполнение + все букеты (букет-extra рендерим тут же — у него полноценный редактор состава) */}
        {items.filter(it => !isSubscription || !it.is_extra || it.bouquet_id).map((item) => {
          const realIdx = items.indexOf(item);
          // === Bouquet item: имя + qty + раскрываемый редактор состава ===
          if (item.bouquet_id) {
            const qtyNum = parseInt(item.quantity) || 1;
            // Сколько собранных экземпляров доступно на дату доставки (из партий-lots).
            // available_physical приходит из batch-доступности; до её загрузки —
            // фолбэк на сохранённый bouquet_kind, чтобы бейдж не мигал «К сборке».
            const physAvail = item.available_physical != null
              ? item.available_physical
              : (item.bouquet_kind === 'assembled' ? qtyNum : 0);
            const comps = item.bouquet_components || [];
            // Модифицированный букет (изменён состав) ВСЕГДА «К сборке» — флорист дорабатывает
            // готовый экземпляр, даже если букет берётся из собранной партии.
            const isModified = item.components_dirty || comps.some(c => c.is_replacement);
            const servedFromLot = Math.min(qtyNum, physAvail);
            const needRecipe = Math.max(0, qtyNum - servedFromLot);
            const isAssembled = !isModified && needRecipe === 0 && servedFromLot > 0;
            const isPartial = !isModified && servedFromLot > 0 && needRecipe > 0;
            const isExpanded = expandedBouquetIdx === realIdx;
            return (
              <div key={realIdx} style={{ padding: '4px 0', borderBottom: '1px solid #EBEDEC' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                      padding: '2px 6px', '--ch': '4px',
                      background: '#FFE7DD', color: '#7E5D00',
                    }}>{capitalizeFirst(singularLower(catalogLabels.bouquets))}</span>
                    <span style={{ fontWeight: 600 }}>{item.product_name}</span>
                    <span style={{
                      fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                      padding: '2px 6px', '--ch': '4px',
                      background: isAssembled ? '#DFF2E8' : isPartial ? '#FBF1E0' : '#F4F4EF',
                      color: isAssembled ? '#1C3D2E' : isPartial ? '#7E5D00' : '#5E6561',
                    }}>{isAssembled ? 'Собран' : isPartial ? `${servedFromLot} собран · ${needRecipe} к сборке` : 'К сборке'}</span>
                    {(item.components_dirty || (item.bouquet_components || []).some(c => c.is_replacement)) && (
                      <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', '--ch': '4px', background: '#F9EBCF', color: '#7E5D00', textTransform: 'uppercase', letterSpacing: 0.4 }}>Состав изменён</span>
                    )}
                    <span style={{ color: '#5E6561', fontSize: 11 }}>{Math.round(item.price)}₽/шт</span>
                  </div>
                  <QtyStepper value={item.quantity} min={1} onChange={q => setItems(prev => prev.map((it, i) => i === realIdx ? { ...it, quantity: q } : it))} />
                  <span style={{ fontSize: 13, fontWeight: 600, minWidth: 70, textAlign: 'right' }}>
                    {Math.round(item.price * item.quantity)}₽
                  </span>
                  {/* Убрать позицию — общая иконка-корзина, как в «Новом заказе» (UX-аудит 25.10) */}
                  <button type="button" className="btn-icon is-danger" onClick={() => handleRemoveItem(realIdx)} title="Убрать" aria-label="Убрать" style={{ flexShrink: 0 }}>
                    <TrashIcon />
                  </button>
                </div>
                {/* Inline deficit: считаем нехватку стеблей ТОЛЬКО на recipe-часть
                    (qty − собранные из партий). Экземпляры из lots уже собраны —
                    стебли для них не нужны, дефицита по ним нет.
                    components_status приходит из batch-availability. backend.available_on_date
                    = stock − other_orders − cart_items_pending (включая полный self-спрос),
                    может быть < 0 при overcommit. Прибавляем полный self_demand обратно
                    (qty), чтобы получить «доступно исключая собственный спрос», затем
                    сравниваем с потребностью recipe-части (needRecipe). */}
                {(() => {
                  const qty = qtyNum;
                  const cs = item.components_status || [];
                  if (!cs.length || needRecipe <= 0) return null;
                  const deficits = cs
                    .map(c => {
                      const perB = c.needed_per_bouquet || 0;
                      const availExclSelf = (c.available_on_date || 0) + perB * qty;
                      const recipeNeeded = perB * needRecipe;
                      const def = Math.max(0, recipeNeeded - Math.max(0, availExclSelf));
                      return def > 0 ? { name: c.name, deficit: def } : null;
                    })
                    .filter(Boolean);
                  if (!deficits.length) return null;
                  return (
                    <div style={{ marginTop: 4, fontSize: 11, color: '#B0603C', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <span style={{ fontWeight: 600 }}>Дефицит:</span>
                      {deficits.map((d, i) => (
                        <span key={i}>{d.name} <strong>{d.deficit}</strong>{i < deficits.length - 1 ? ',' : ''}</span>
                      ))}
                    </div>
                  );
                })()}
                {item.assembled_only && needRecipe > 0 && (
                  <div style={{ marginTop: 4, fontSize: 11, color: '#7E5D00' }}>
                    <span style={{ fontWeight: 600 }}>Только готовые:</span> ещё {needRecipe} шт соберётся по рецепту
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setExpandedBouquetIdx(isExpanded ? null : realIdx)}
                  className="btn-link"
                  style={{ marginTop: 4, fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <span style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.15s', display: 'inline-block' }}>▾</span>
                  {isExpanded ? 'Скрыть состав' : `Изменить состав (${comps.length} поз.)`}
                </button>
                {isExpanded && (
                  <div style={{ marginTop: 6, padding: 8, background: '#F4F4EF', '--ch': '5px', '--stroke': '#D8DDDA', border: '1px solid #D8DDDA' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: '#909894', marginBottom: 4 }}>{`Состав на 1 ${singularLower(catalogLabels.bouquets)}`}</div>
                    {comps.map((c, ci) => (
                      <div key={ci} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}>
                        <div style={{ flex: 1, fontSize: 12, color: c.is_replacement ? '#7E5D00' : '#1C3D2E' }}>
                          {c.is_replacement && <span style={{ fontWeight: 700 }}>≠ </span>}
                          {c.product_name}
                          <span style={{ color: '#5E6561', marginLeft: 4, fontSize: 10 }}>{c.product_price}₽/шт</span>
                        </div>
                        <QtyStepper value={c.quantity} min={1} onChange={q => handleBouquetComponentQty(realIdx, ci, q)} />
                        <span style={{ fontSize: 13, fontWeight: 600, minWidth: 70, textAlign: 'right' }}>
                          {Math.round((c.product_price || 0) * (c.quantity || 0))}₽
                        </span>
                        <button type="button" className="btn-icon is-danger" onClick={() => handleRemoveBouquetComponent(realIdx, ci)} title="Убрать" aria-label="Убрать" style={{ flexShrink: 0 }}>
                          <TrashIcon />
                        </button>
                      </div>
                    ))}
                    {showCompSearchForIdx === realIdx ? (
                      <div style={{ marginTop: 4 }}>
                        <ProductSearchDropdown
                          onSelect={(product) => handleAddBouquetComponent(realIdx, product)}
                          excludeIds={comps.map(c => c.product_id)}
                          onClose={() => setShowCompSearchForIdx(null)}
                          maxQtyMap={maxQtyMap}
                          products={allProductsList}
                        />
                      </div>
                    ) : (
                      <button onClick={() => setShowCompSearchForIdx(realIdx)} type="button" className="btn-link" style={{ marginTop: 4, fontSize: 11 }}>
                        + Добавить стебель
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          }
          // === Product item ===
          // База подписки (наполнение) read-only: состав меняется только через «Наполнить»,
          // здесь qty/удаление заблокированы (бэкенд это тоже гейтит 400-кой).
          const lockedBase = isSubscription && !item.is_extra;
          const available = getAvailable(item.product_id);
          const atMax = item.quantity >= available;
          return (
          <div key={realIdx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', borderBottom: '1px solid #EBEDEC' }}>
            <div style={{ flex: 1, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{
                fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                padding: '2px 6px', '--ch': '4px',
                background: '#DFF2E8', color: '#1C3D2E',
              }}>{catalogLabels.products}</span>
              <span>{item.product_name}</span>
              <span style={{ color: '#5E6561' }}>{item.price}₽</span>
              {!lockedBase && (
                <span style={{ color: atMax ? '#B0603C' : '#5E6561', fontSize: 11 }}>(доступно: {Math.max(0, available - (parseInt(item.quantity) || 0))})</span>
              )}
              {(item.step || 1) > 1 && (
                <span style={{ color: '#1C3D2E', fontSize: 11 }}>(шаг: {item.step})</span>
              )}
            </div>
            {lockedBase ? (
              <span
                title="Состав наполнения меняется через «Наполнить»"
                style={{ fontSize: 13, fontWeight: 600, color: '#5E6561', minWidth: 50, textAlign: 'center' }}
              >×{item.quantity}</span>
            ) : (
              <>
                {/* Админ правит по одной штуке: шаг — правило витрины для клиента, здесь он
                    только справка «(шаг: N)». Раньше «−» при остатке = шагу упирался в 1. */}
                <QtyStepper value={item.quantity} min={1} onChange={q => setItems(prev => prev.map((it, i) => i === realIdx ? { ...it, quantity: q } : it))} />
              </>
            )}
            <span style={{ fontSize: 13, fontWeight: 600, minWidth: 70, textAlign: 'right' }}>
              {item.price * item.quantity}₽
            </span>
            {!lockedBase && (
              <button type="button" className="btn-icon is-danger" onClick={() => handleRemoveItem(realIdx)} title="Убрать" aria-label="Убрать" style={{ flexShrink: 0 }}>
                <TrashIcon />
              </button>
            )}
          </div>
          );
        })}
        {isSubscription && items.some(it => it.is_extra && !it.bouquet_id) && (
          <div style={{ fontSize: 11, color: '#1C3D2E', fontWeight: 600, marginTop: 8, marginBottom: 2, paddingTop: 6, borderTop: '1px dashed #D8DDDA' }}>Дополнительные товары</div>
        )}
        {isSubscription && items.filter(it => it.is_extra && !it.bouquet_id).map((item) => {
          const realIdx = items.indexOf(item);
          const available = getAvailable(item.product_id);
          const atMax = item.quantity >= available;
          return (
          <div key={realIdx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', borderBottom: '1px solid #EBEDEC' }}>
            <div style={{ flex: 1, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{
                fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                padding: '2px 6px', '--ch': '4px',
                background: '#DFF2E8', color: '#1C3D2E',
              }}>{catalogLabels.products}</span>
              <span>{item.product_name}</span>
              <span style={{ color: '#5E6561' }}>{item.price}₽</span>
              <span style={{ color: atMax ? '#B0603C' : '#5E6561', fontSize: 11 }}>(доступно: {Math.max(0, available - (parseInt(item.quantity) || 0))})</span>
              {(item.step || 1) > 1 && (
                <span style={{ color: '#1C3D2E', fontSize: 11 }}>(шаг: {item.step})</span>
              )}
            </div>
            <QtyStepper value={item.quantity} min={1} onChange={q => setItems(prev => prev.map((it, i) => i === realIdx ? { ...it, quantity: q } : it))} />
            <span style={{ fontSize: 13, fontWeight: 600, minWidth: 70, textAlign: 'right' }}>
              {item.price * item.quantity}₽
            </span>
            <button type="button" className="btn-icon is-danger" onClick={() => handleRemoveItem(realIdx)} title="Убрать" aria-label="Убрать" style={{ flexShrink: 0 }}>
              <TrashIcon />
            </button>
          </div>
          );
        })}
        {showProductSearch ? (
          <div style={{ marginTop: 6 }}>
            <ProductSearchDropdown
              onSelect={handleAddProduct}
              excludeIds={items.filter(it => !it.bouquet_id).map(it => it.product_id)}
              onClose={() => setShowProductSearch(false)}
              selectedDate={deliveryDate || null}
              maxQtyMap={maxQtyMap}
              products={allProductsList}
            />
          </div>
        ) : showBouquetSearch ? (
          <div style={{ marginTop: 6 }}>
            <BouquetSearchDropdown
              onSelect={handleAddBouquet}
              onClose={() => setShowBouquetSearch(false)}
              deliveryDate={deliveryDate}
              excludeOrderId={order?.id || null}
              excludeBouquetIds={items.filter(it => it.bouquet_id).map(it => it.bouquet_id)}
              cartItemsPending={items.map(it => it.bouquet_id ? ({
                bouquet_id: it.bouquet_id,
                quantity: it.quantity,
                components: (it.bouquet_components || []).map(c => ({ product_id: c.product_id, quantity: c.quantity })),
              }) : ({
                product_id: it.product_id,
                quantity: it.quantity,
              }))}
            />
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            <button onClick={() => setShowProductSearch(true)} type="button" className="btn-link" style={{ fontSize: 13 }}>
              + Добавить стебли
            </button>
            <button onClick={() => setShowBouquetSearch(true)} type="button" className="btn-link" style={{ fontSize: 13 }}>
              + Добавить букет
            </button>
            <button onClick={handleAddAssembly} type="button" className="btn-link" style={{ fontSize: 13 }}>
              + Добавить сборный букет
            </button>
          </div>
        )}

        {/* Сборные букеты */}
        {assemblies.length > 0 && (
          <div style={{ marginTop: 10 }}>
            {assemblies.map((grp, gIdx) => (
              <AssemblyGroupBuilder
                key={`asm-edit-${gIdx}`}
                sequenceNo={gIdx + 1}
                items={grp.items}
                floristNotes={grp.florist_notes}
                selectedDate={deliveryDate || null}
                onAddStem={(s) => handleAssemblyAddStem(gIdx, s)}
                onRemoveStem={(sIdx) => handleAssemblyRemoveStem(gIdx, sIdx)}
                onChangeQty={(sIdx, q) => handleAssemblyChangeQty(gIdx, sIdx, q)}
                onChangeNotes={(notes) => handleAssemblyChangeNotes(gIdx, notes)}
                onRemoveGroup={() => handleRemoveAssembly(gIdx)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Delivery + Date/Time — один блок, две колонки */}
      <div style={sectionStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Левая колонка: тип доставки + адрес/самовывоз */}
          <div>
            <div style={{ ...sectionTitleStyle, color: '#1C3D2E' }}>Тип доставки</div>
            <div className="seg" style={{ display: 'flex', marginBottom: 8 }}>
              <button type="button"
                onClick={() => handleDeliveryTypeChange('delivery')}
                className={`seg-btn${deliveryType === 'delivery' ? ' on' : ''}`} style={{ flex: 1 }}
              >Доставка</button>
              <button type="button"
                onClick={() => handleDeliveryTypeChange('pickup')}
                className={`seg-btn${deliveryType === 'pickup' ? ' on' : ''}`} style={{ flex: 1 }}
              >Самовывоз</button>
            </div>

            {deliveryType === 'delivery' && (
              <>
                <div style={{ marginBottom: 6 }}>
                  <label style={labelStyle}>Адрес</label>
                  <AdminAddressAutocomplete value={address} onChange={handleAddressChange} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <div>
                    <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
                      Квартира
                      <label style={{ fontSize: 11, color: '#5E6561', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2, fontWeight: 400 }}>
                        <input type="checkbox" checked={noApartment} onChange={e => { setNoApartment(e.target.checked); if (e.target.checked) setApartment(''); }} style={{ accentColor: '#1C3D2E' }} />
                        нет
                      </label>
                    </label>
                    <input value={apartment} onChange={e => setApartment(e.target.value)} style={inputStyle} disabled={noApartment} />
                  </div>
                  <div>
                    <label style={labelStyle}>Подъезд</label>
                    <input value={entrance} onChange={e => setEntrance(e.target.value)} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Этаж</label>
                    <input value={floor} onChange={e => setFloor(e.target.value)} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Домофон</label>
                    <input value={intercom} onChange={e => setIntercom(e.target.value)} style={inputStyle} />
                  </div>
                </div>
                <label style={{ marginTop: 8, display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#1C3D2E', cursor: 'pointer' }}>
                  <input type="checkbox" checked={hideAddress} onChange={e => setHideAddress(e.target.checked)} style={{ accentColor: '#1C3D2E', marginTop: 2 }} />
                  <span>
                    Не показывать адрес клиенту
                    <span style={{ display: 'block', fontSize: 12, color: '#909894' }}>
                      Адрес уточнили у получателя — заказчик в «Моих заказах» увидит только «Доставка». Флорист и курьер видят адрес как обычно
                    </span>
                  </span>
                </label>
                <div style={{ marginTop: 8, fontSize: 13, color: '#5E6561', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span>Стоимость:</span>
                  {deliveryCostManual ? (
                    <>
                      {/* вне сетки цены нет — поле пустое, а не «0» (иначе ввод «700» даёт «0700») */}
                      <input type="number" min="0" value={deliveryOutOfRange && deliveryCost === 0 ? '' : deliveryCost} placeholder="0"
                        onChange={e => {
                          const v = e.target.value;
                          setDeliveryCost(v === '' ? '' : parseInt(v) || 0);
                        }}
                        onBlur={() => { if (deliveryCost === '' || deliveryCost === 0) setDeliveryCost(0); }}
                        style={{ width: 80, padding: '4px 8px', border: '1px solid #D8DDDA', '--ch': '5px', '--stroke': '#D8DDDA', fontSize: 13, textAlign: 'center' }} />
                      <span>₽</span>
                    </>
                  ) : (
                    <strong style={{ color: '#1C3D2E' }}>{deliveryCalcLoading ? 'Расчёт...' : `${deliveryCost} ₽`}</strong>
                  )}
                  <label style={{ fontSize: 11, color: '#5E6561', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                    <input type="checkbox" checked={deliveryCostManual} onChange={e => setDeliveryCostManual(e.target.checked)} style={{ accentColor: '#1C3D2E' }} />
                    вручную
                  </label>
                </div>
                {deliveryOutOfRange && (
                  <div style={{ fontSize: 12, color: '#B0603C', marginTop: 4 }}>
                    {deliveryOutOfRangeText(deliveryOutOfRange)}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Правая колонка: дата + время */}
          <div>
            <div style={{ ...sectionTitleStyle, color: '#1C3D2E' }}>Дата и время</div>
            <div style={{ marginBottom: 6 }}>
              <label style={labelStyle}>Дата доставки</label>
              <div style={{ position: 'relative' }} ref={calendarRef}>
                <div
                  onClick={() => setShowCalendar(!showCalendar)}
                  style={{
                    ...inputStyle, cursor: 'pointer', display: 'flex',
                    justifyContent: 'space-between', alignItems: 'center'
                  }}
                >
                  <span>{deliveryDate ? formatDisplayDate(deliveryDate) : 'Выберите дату'}</span>
                  <span style={{ color: '#5E6561', fontSize: 11 }}>▼</span>
                </div>
                {showCalendar && (
                  <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 1000, marginTop: 4 }}>
                    <AdminCalendar
                      selectedDate={deliveryDate}
                      onSelectDate={handleDateChange}
                      minDate={todayStr}
                      disabledDates={isInternal ? [] : fullyBlockedDates}
                      coloredDates={isInternal ? {} : coloredDates}
                    />
                  </div>
                )}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Время</label>
              {blockedSlotsForDate === 'all' ? (
                <div style={{ fontSize: 12, color: '#B0603C', padding: '4px 0' }}>Все слоты заблокированы на эту дату</div>
              ) : (
                /* Слоты — один переключатель .seg с переносом: срез только у контейнера,
                   между слотами разделители, без промежутков (владелец 25.10) */
                <div className={`seg seg-grid c${slotGridCols(configSlots.length + (deliveryTime && !configSlots.includes(deliveryTime) ? 1 : 0))}`} role="group" aria-label="Время доставки">
                  {/* Слот заказа может отсутствовать в актуальной сетке (сетка сменилась
                      после оформления) — подмешиваем его, иначе правка потеряет время */}
                  {(deliveryTime && !configSlots.includes(deliveryTime)
                    ? [...configSlots, deliveryTime].sort((a, b) => slotStartMinutes(a) - slotStartMinutes(b))
                    : configSlots
                  ).map(ts => {
                    const isArchival = !configSlots.includes(ts);
                    const isBlocked = Array.isArray(blockedSlotsForDate) && blockedSlotsForDate.includes(ts);
                    const isPast = deliveryDate === todayStr && parseInt(ts.split('-')[1].split(':')[0], 10) <= new Date().getHours();
                    const disabled = isBlocked || isPast;
                    const selected = deliveryTime === ts;
                    return (
                      <button
                        key={ts}
                        type="button"
                        onClick={() => !disabled && setDeliveryTime(selected ? '' : ts)}
                        disabled={disabled}
                        className={`seg-btn${selected ? ' on' : ''}`}
                        style={{
                          whiteSpace: 'nowrap', padding: '7px 6px',
                          ...(disabled ? { cursor: 'not-allowed' } : null),
                          ...(disabled && !selected ? { color: '#A9B1AC' } : null),
                          ...(isBlocked ? { textDecoration: 'line-through' } : null),
                        }}
                      >
                        {ts}{isArchival ? ' (архивный)' : ''}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recipient */}
      <div style={sectionStyle}>
        <div style={{ ...sectionTitleStyle, color: '#1C3D2E' }}>Заказчик и получатель</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
          <div>
            <label style={labelStyle}>Имя заказчика</label>
            <input value={customerName} onChange={e => setCustomerName(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Телефон заказчика</label>
            <input value={displayPhone(phone)} onChange={e => handlePhoneChange(e.target.value, phone, setPhone)} style={inputStyle} placeholder="+7 (___) ___-__-__" />
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', marginBottom: isRecipientOther ? 6 : 0 }}>
          <input type="checkbox" checked={isRecipientOther} onChange={e => setIsRecipientOther(e.target.checked)} style={{ accentColor: '#1C3D2E' }} />
          Получатель — другой человек
        </label>
        {isRecipientOther && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <div>
              <label style={labelStyle}>Имя получателя</label>
              <input value={recipientName} onChange={e => setRecipientName(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Телефон получателя</label>
              <input value={displayPhone(recipientPhone)} onChange={e => handlePhoneChange(e.target.value, recipientPhone, setRecipientPhone)} style={inputStyle} placeholder="+7 (___) ___-__-__" />
            </div>
          </div>
        )}
      </div>

      {/* Postcard & Options */}
      <div style={sectionStyle}>
        <div style={{ ...sectionTitleStyle, color: '#1C3D2E' }}>Опции</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', marginBottom: hasPostcard ? 6 : 4 }}>
          <input type="checkbox" checked={hasPostcard} onChange={e => handlePostcardToggle(e.target.checked)} style={{ accentColor: '#1C3D2E' }} />
          Открытка ({settings.postcard_price} ₽)
        </label>
        {hasPostcard && (
          <textarea
            value={greeting}
            onChange={e => setGreeting(e.target.value)}
            placeholder="Текст открытки..."
            style={{ ...inputStyle, minHeight: 50, resize: 'vertical', marginBottom: 6 }}
          />
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', marginBottom: 6 }}>
          <input type="checkbox" checked={oddQuantity} onChange={e => setOddQuantity(e.target.checked)} style={{ accentColor: '#1C3D2E' }} />
          Нечётное количество
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: '#5E6561' }}>Упаковка:</span>
          <div className="seg">
            <button type="button" onClick={() => setPackagingType('none')}
              className={`seg-btn${packagingType === 'none' ? ' on' : ''}`}>Без коробки</button>
            {/* Типы из каталога упаковок; скрытые не предлагаем, кроме уже выбранной в заказе.
                Кнопка с нулевым остатком выключена, но выбранная в заказе остаётся активной. */}
            {packagingList
              .filter(p => !p.is_hidden || p.type === packagingType)
              .map(pkg => (
                <button key={pkg.type} type="button" onClick={() => setPackagingType(pkg.type)}
                  className={`seg-btn${packagingType === pkg.type ? ' on' : ''}`}
                  style={pkg.quantity <= 0 ? { opacity: 0.4 } : undefined}
                  disabled={pkg.quantity <= 0 && packagingType !== pkg.type}>
                  {pkg.name || pkg.type} ({pkg.quantity})
                </button>
              ))}
          </div>
        </div>
      </div>

      {/* Client payment amount (internal only) */}
      {isInternal && (
        <div style={sectionStyle}>
          <div style={{ ...sectionTitleStyle, color: '#B0603C' }}>Сумма к оплате</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="number" min="0" value={clientPaymentAmount}
              onChange={e => setClientPaymentAmount(e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
              onBlur={() => { if (clientPaymentAmount === '') setClientPaymentAmount(0); }}
              style={{ width: 120, padding: '6px 10px', border: '1px solid #D8DDDA', '--ch': '5px', '--stroke': '#D8DDDA', fontSize: 14, fontWeight: 600 }} />
            <span style={{ color: '#5E6561' }}>₽</span>
            <span style={{ fontSize: 11, color: '#5E6561' }}>0 = оплата не требуется</span>
          </div>
        </div>
      )}

      {/* Расчёт — тот же вид, что в карточке заказа и букета (спецификация дизайнера 07.10):
          белая карточка, изменения сверху, строки расчёта, полоса «Итого» и под ней «Доплата»/«Возврат» */}
      <div style={{ ...sectionStyle, padding: 0 }}>
        {changes.length > 0 && (
          <div style={{ padding: '10px 16px', borderBottom: '1px solid #EBEDEC' }}>
            <div style={{ fontSize: 12, color: '#5E6561', marginBottom: 3 }}>Изменения</div>
            {changes.map((c, i) => (
              <div key={i} style={{ fontSize: 12, color: c.type === 'add' ? '#1C3D2E' : '#B0603C', padding: '1px 0' }}>
                {c.type === 'add' ? '+ ' : c.type === 'remove' ? '− ' : '~ '}{c.text}
              </div>
            ))}
          </div>
        )}
        <div style={{ padding: '10px 16px 8px', fontSize: 13, display: 'flex', flexDirection: 'column' }}>
          {isSubscription ? (
            <>
              {/* Подписка: стоимость + скидка + сервисный сбор */}
              <SumRow label="Стоимость подписки" value={rub(order?.subscription_price_per_delivery)} />
              {subDiscountAmount > 0 && (
                <SumRow label={`Скидка подписки ${order?.subscription_discount_percent}%`} value={`−${rub(subDiscountAmount)}`} />
              )}
              <SumRow label={`Сервисный сбор · ${feePercent} %`} value={rub(((order?.subscription_price_per_delivery || 0) - subDiscountAmount) * feePercent / 100)} />
              {bonusUsed > 0 && <SumRow label={`Со счёта ${BRAND_NAME}`} value={`−${rub(bonusUsed)}`} />}
              {/* Дополнительные товары: стебли по единой ставке, букеты по букетной */}
              {extraCostForSub > 0 && (
                <>
                  <SumRow label="Дополнительно" value={rub(extraCostForSub)} strong />
                  {extraStemsForSub > 0 && (
                    <SumRow sub label={`· ${catalogLabels.products} ${feePercent} %`} value={rub(extraStemsForSub * feePercent / 100)} />
                  )}
                  {extraBouquetsForSub > 0 && (
                    <SumRow sub label={`· Букеты ${bouquetsFeePct} %`} value={rub(extraBouquetsForSub * bouquetsFeePct / 100)} />
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <SumRow label="Товары" value={rub(subtotal)} />
              {currentPostcardPrice > 0 && <SumRow label="Открытка" value={rub(currentPostcardPrice)} />}
              {/* Сервисный сбор + детализация по типам (стебли/букеты). preview-edit
                  возвращает разнесённые stems_service_fee/bouquets_service_fee. */}
              <SumRow label="Сервисный сбор" value={rub(effServiceFee)} />
              {preview && preview.stems_service_fee > 0 && (
                <SumRow sub label={`· ${catalogLabels.products} ${preview.stems_service_fee_percent} %`} value={rub(preview.stems_service_fee)} />
              )}
              {preview && preview.bouquets_service_fee > 0 && (
                <SumRow sub label={`· ${catalogLabels.bouquets} ${preview.bouquets_service_fee_percent} %`} value={rub(preview.bouquets_service_fee)} />
              )}
              <SumRow label="Доставка" value={rub(deliveryCost)} />
              {bonusUsed > 0 && <SumRow label={`Со счёта ${BRAND_NAME}`} value={`−${rub(bonusUsed)}`} />}
            </>
          )}
          {isInternal && currentClientPayment != null && (
            <SumRow label="К оплате клиентом" value={rub(currentClientPayment)} accent />
          )}
          {diff !== 0 && (
            <SumRow label="Было" value={isInternal ? rub(origClientPayment != null ? origClientPayment : 0) : rub(origTotal)} muted />
          )}
        </div>
        <div style={{ background: '#DFF2E8', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: '#1C3D2E' }}>Итого</span>
          <span style={{ fontWeight: 800, fontSize: 20, color: '#1C3D2E', fontVariantNumeric: 'tabular-nums' }}>{rub(effTotal - bonusUsed)}</span>
        </div>
        {diff !== 0 && order?.payment_status !== 'not_required' && (
          <div style={{
            background: diff > 0 ? '#FFE7DD' : '#F4F4EF', padding: '10px 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            color: diff > 0 ? '#B0603C' : '#1C3D2E',
          }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>{diff > 0 ? 'Доплата' : 'Возврат'}</span>
            <span style={{ fontWeight: 800, fontSize: 18, fontVariantNumeric: 'tabular-nums' }}>{diff > 0 ? `+${rub(diff)}` : rub(diff)}</span>
          </div>
        )}
      </div>

      {/* Источники резервирования резолвятся бэком автоматически (resolve_stock_sources) */}

      {/* Actions — кнопка "Сохранить изменения" рендерится в footer Modal через ref.triggerSave() */}

      {/* Date warning dialog */}
      {showDateWarning && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: '#fff', '--ch': '10px', padding: 24, maxWidth: 380, width: '90%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 10, color: '#B0603C' }}>
              Нет стока на выбранную дату
            </div>
            <div style={{ fontSize: 14, color: '#1C3D2E', marginBottom: 16, lineHeight: 1.5 }}>
              На выбранную дату не хватает остатка для одной или нескольких позиций заказа.
              Изменение согласовано с управляющим?
            </div>
            {/* Как во всех окнах: [Отмена] [Главная], без «Да/Нет» (UX-аудит 25.10) */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary btn-md" onClick={cancelDateChange}>
                Отмена
              </button>
              <button type="button" className="btn btn-primary btn-md" onClick={confirmDateChange}>
                Продолжить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Диалог подтверждения дефицита */}
      {deficitConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', '--ch': '10px', padding: '24px 28px', maxWidth: 420, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: '#B0603C' }}>Товары с дефицитом</div>
            <div style={{ fontSize: 14, color: '#1C3D2E', marginBottom: 16 }}>
              {(deficitConfirm.groups || []).map((g, gi) => (
                <div key={`g${gi}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', padding: '6px 0', borderBottom: '1px solid #EBEDEC', alignItems: 'start' }}>
                  <div style={{ fontWeight: 600 }}>{g.title}</div>
                  <div>
                    {g.lines.map((l, li) => (
                      <div key={li} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '1px 0' }}>
                        <span>{l.name}</span>
                        <span style={{ color: '#B0603C', fontWeight: 600, whiteSpace: 'nowrap' }}>нет {l.deficit} шт</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {deficitConfirm.names.map((n, i) => (
                <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid #EBEDEC' }}>• {n}</div>
              ))}
            </div>
            <div style={{ fontSize: 13, color: '#5E6561', marginBottom: 16 }}>
              Недостающие позиции будут помечены как дефицит в аналитике покрытия.
            </div>
            {/* Как окно «Нет стока»: [Отмена] [Главная]; терракота — акцент, не кнопка (UX-аудит 25.10) */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary btn-md" onClick={() => setDeficitConfirm(null)}>
                Отмена
              </button>
              <button type="button" className="btn btn-primary btn-md" onClick={deficitConfirm.onConfirm}>
                Сохранить с дефицитом
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

const labelStyle = { display: 'block', fontSize: 11, color: '#5E6561', marginBottom: 2, fontWeight: 500 };
const inputStyle = { width: '100%', padding: '7px 10px', fontSize: 13, border: '1px solid #D8DDDA', '--ch': '5px', '--stroke': '#D8DDDA', outline: 'none', boxSizing: 'border-box', color: '#1C3D2E', background: '#fff' };
// Строка расчёта (подпись слева, сумма справа) — общий вид с карточкой заказа и букета
const rub = (n) => `${Math.round(Number(n) || 0).toLocaleString('ru-RU')} ₽`;
function SumRow({ label, value, sub = false, muted = false, strong = false, accent = false }) {
  const labelColor = accent ? '#9A4820' : sub || muted ? '#909894' : '#5E6561';
  const valueColor = accent ? '#9A4820' : sub || muted ? '#909894' : '#1C3D2E';
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', padding: '3px 0',
      fontSize: sub ? 12 : 13, paddingLeft: sub ? 12 : 0,
    }}>
      <span style={{ color: labelColor, fontWeight: strong || accent ? 600 : 400 }}>{label}</span>
      <span style={{ color: valueColor, fontWeight: sub || muted ? 400 : strong || accent ? 600 : 500, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}

export default OrderEditPanel;
